import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { format } from "date-fns";
import _ from "lodash";
import {
  consultationChartDataProcessor,
  revenueChartDataProcessor,
} from "../helpers/utilities";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: false,
      text: "Consultations x Revenue Chart",
    },
  },
};

/**
 *
 * @param {array} consultations list of consultations
 * @returns
 */
export function ConsultationsChart({
  consultations,
  revenue,
  chartType = "bar",
}) {
  const { data } = consultationChartDataProcessor(consultations);
  const { data: revenueData } = revenueChartDataProcessor(revenue);

  const labels = data.map((d) => format(new Date(d.date), "EEE, MMM d"));

  const conValues = data.map((d) => d.count);
  const revValues = revenueData.map((d) => d.sum);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Consultations",
        data: conValues,
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
      },
      {
        label: "Revenue",
        data: revValues,
        borderColor: "rgb(255, 65, 84)",
        backgroundColor: "rgba(255, 65, 84, 0.5)",
        tension: 0.2,
        borderDash: [3, 2],
      },
    ],
  };

  return chartType === "bar" ? (
    <Bar options={options} data={chartData} />
  ) : (
    <Line options={options} data={chartData} />
  );
  // <Chart type={ chartType } options={ options } data={ chartData } /> );
}

const careValueCenterTextPlugin = {
  id: "careValueCenterText",
  beforeDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const pct = opts?.usedPercent ?? 0;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "600 1.75rem system-ui, sans-serif";
    ctx.fillStyle = "#212529";
    ctx.fillText(`${pct}%`, centerX, centerY - 6);
    ctx.font = "500 0.75rem system-ui, sans-serif";
    ctx.fillStyle = "#6c757d";
    ctx.fillText("Used", centerX, centerY + 16);
    ctx.restore();
  },
};

const formatGhs = (value) =>
  `GHS ${Number(value || 0).toLocaleString("en-GH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

/**
 * Donut for membership care value: used vs balance of total allocated, with used % in center.
 */
export function CareValueChart({ benefits = [] }) {
  const totals = benefits.reduce(
    (acc, benefit) => {
      const allocated = benefit.totalBenefits || 0;
      const balance = benefit.balance || 0;
      const used = Math.max(0, allocated - balance);
      acc.allocated += allocated;
      acc.used += used;
      acc.balance += balance;
      return acc;
    },
    { allocated: 0, used: 0, balance: 0 }
  );

  const usedPercent =
    totals.allocated > 0
      ? Math.round((totals.used / totals.allocated) * 100)
      : 0;

  if (totals.allocated <= 0) {
    return (
      <div className="text-center text-muted small py-4">
        No care value allocated to chart
      </div>
    );
  }

  const chartData = {
    labels: ["Used", "Balance"],
    datasets: [
      {
        data: [totals.used, totals.balance],
        backgroundColor: [
          "rgba(220, 53, 69, 0.88)",
          "rgba(25, 135, 84, 0.88)",
        ],
        borderColor: "#fff",
        borderWidth: 3,
        hoverOffset: 6,
      },
    ],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed ?? 0;
            const share =
              totals.allocated > 0
                ? Math.round((value / totals.allocated) * 100)
                : 0;
            return `${context.label}: ${formatGhs(value)} (${share}% of allocated)`;
          },
        },
      },
      careValueCenterText: { usedPercent },
    },
  };

  return (
    <div>
      <div style={{ height: 260, maxWidth: 320, margin: "0 auto" }}>
        <Doughnut
          data={chartData}
          options={donutOptions}
          plugins={[careValueCenterTextPlugin]}
        />
      </div>
      <div className="row g-2 mt-2 text-center small">
        <div className="col-4">
          <div
            className="text-muted text-uppercase fw-semibold mb-1"
            style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}>
            Allocated
          </div>
          <div className="fw-semibold" style={{ color: "#212529" }}>
            {formatGhs(totals.allocated)}
          </div>
        </div>
        <div className="col-4">
          <div
            className="text-muted text-uppercase fw-semibold mb-1"
            style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}>
            Used
          </div>
          <div className="fw-semibold text-danger">
            {formatGhs(totals.used)}
          </div>
          <div className="text-muted" style={{ fontSize: "0.7rem" }}>
            {usedPercent}% of allocated
          </div>
        </div>
        <div className="col-4">
          <div
            className="text-muted text-uppercase fw-semibold mb-1"
            style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}>
            Balance
          </div>
          <div className="fw-semibold text-success">
            {formatGhs(totals.balance)}
          </div>
        </div>
      </div>
    </div>
  );
}
